import React, { useEffect, useState } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { get } from '../../util/api';

import {
  Page,
  LayoutSingleColumn,
  Button,
  H2,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './PricingPage.module.css';

/**
 * Public pricing page.
 *
 * This page exists so that anyone (e.g. a Stripe reviewer who cannot log in
 * through Telia) can see the available subscription plans and prices without
 * authenticating. It intentionally uses the public single-column layout
 * instead of the account side-navigation, and it does NOT create a Stripe
 * subscription on its own — the actual payment flow still lives behind
 * authentication in `/account/prices`.
 */
const PricingPageComponent = props => {
  const { scrollingDisabled, isAuthenticated } = props;

  const [prices, setPrices] = useState([]);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPrices = async () => {
      try {
        const { prices } = await get('/api/get-subscription-prices');
        if (isMounted) {
          setPrices(prices || []);
        }
      } catch (e) {
        console.error('Failed to load subscription prices', e);
      }
    };
    fetchPrices();
    return () => {
      isMounted = false;
    };
  }, []);

  // Where a "Choose plan" click should send the visitor.
  // - Logged-out visitors (incl. the Stripe reviewer) have no account yet,
  //   so they must sign up first.
  // - Logged-in visitors continue to the real, authenticated subscribe flow,
  //   which creates the Stripe subscription and renders the card form.
  const getCtaTarget = () => (isAuthenticated ? '/account/prices' : '/signup');

  const handleSelectPlan = () => {
    setRedirectTo(getCtaTarget());
  };

  if (redirectTo) {
    return <Redirect to={{ pathname: redirectTo }} />;
  }

  const formatPrice = price => {
    const amount = (price.unit_amount || 0) / 100;
    const currency = (price.currency || 'eur').toUpperCase();
    const interval = price.recurring?.interval;
    return interval
      ? `${amount} ${currency} / ${interval}`
      : `${amount} ${currency}`;
  };

  return (
    <Page title="Pricing" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <H2 as="h1" className={css.heading}>
            Subscription plans
          </H2>
          <p className={css.subHeading}>
            Choose a plan to get started. You'll create an account before payment.
          </p>

          <div className={css.plans}>
            {prices.length === 0 ? (
              <p className={css.empty}>Loading plans…</p>
            ) : (
              prices.map(price => (
                <div key={price.id} className={css.plan}>
                  <h3 className={css.planName}>{price.nickname || 'Plan'}</h3>
                  <p className={css.price}>{formatPrice(price)}</p>
                  <Button className={css.cta} onClick={handleSelectPlan}>
                    Choose plan
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

PricingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  isAuthenticated: bool.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    isAuthenticated,
  };
};

const PricingPage = compose(connect(mapStateToProps))(PricingPageComponent);

export default PricingPage;
